import React, { useEffect, useReducer } from 'react';
import Form from './Form';
import { axiosUpdateAccountHolderInformation } from '../../actions/userActions';
import {
    convertPICNumberListingToAReadableFormat,
    convertUserDetailedInformationToAReadableFormat,
} from '../../selectors/converterSelector';
import { handleAxiosErrors } from '../../_utils';
import { axiosRequestCustomer } from '../../actions/customerActions';
import { createAlert } from '../../dom/window';
import { getTranslation } from '../../lang/main';
import {
    axiosDeletePICNumber,
    axiosRequestPICNumbers,
    axioxMakeDefaultPICNumber,
} from '../../actions/picNumberActions';
import {
    axiosCreateAddress,
    axiosDeleteAddress,
    axiosRequestAddresses,
    axiosRequestDefaultBillingAddressID,
} from '../../actions/user/address';
import { getUserFullInfo } from '../../selectors/userSelector';
// import PropTypes from 'prop-types';

const initialData = {
    isLoading: {
        basicInfo: false,
        supplierAddresses: false,
        wholesalerResellerList: false,
        addressList: false,
        postmenList: false,
        cartRuleList: false,
        picNumberList: false,
        creditCardList: false,
    },
    initialized: false,
    info: {
        basicInfo: {},
        availableWholesalerReseller: [],
        availablePICNumbers: [],
        availableCreditCards: [],
        availableAddresses: [],
    },
};

const customerDetailFormReducer = (state, action) => {
    switch (action.type) {
        case 'TOGGLE_USER_LOADING': {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    [action.key]: action.value,
                },
            };
        }
        case 'SET_USER_INITIALIZED': {
            return {
                ...state,
                initialized: action.value,
            };
        }
        case 'SET_USER_INFO': {
            let newInfo = {
                ...state.info,
                ...action.value,
            };

            const prevBasicInfo = state.info.basicInfo;
            if(prevBasicInfo !== undefined && prevBasicInfo) {
                newInfo.basicInfo = {
                    ...prevBasicInfo,
                    ...newInfo.basicInfo,
                };
            }

            return {
                ...state,
                info: newInfo,
            };
        }
        default: {
            throw new Error(
                `Unhandled action type: ${
                    action.type
                }. Please fix it. Thank you.`
            );
        }
    }
};

/**
 * Customer Detail Form (Container)
 * This tries to mimic AccountDetailFormContainer as much as possible
 *
 * @param props
 * @returns {*}
 * @constructor
 */
function CustomerDetailForm(props) {
    const [state, dispatch] = useReducer(
        customerDetailFormReducer,
        initialData
    );

    const { addressList, setAddressList, globalConfig, memberType } = props;
    const customerID = props.id;

    const toggleLoading = (key, value) => {
        return {
            type: 'TOGGLE_USER_LOADING',
            key: key,
            value: value,
        };
    };

    const setUserInfo = (value) => {
        if (typeof value.availableAddresses != 'undefined') {
            setAddressList(value.availableAddresses);
            value.availableAddresses = value.availableAddresses.map(
                (item) => item.ID
            );
        }

        return {
            type: 'SET_USER_INFO',
            value: value,
        };
    };

    const setInitialized = (value) => {
        return {
            type: 'SET_USER_INITIALIZED',
            value: value,
        };
    };

    const requestInfo = () => {
        dispatch(toggleLoading('basicInfo', true));

        return axiosRequestCustomer(customerID)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );

                dispatch(setUserInfo(userInfo));
                dispatch(setInitialized(true));
                dispatch(toggleLoading('basicInfo', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('basicInfo', false));
            });
    };
    const requestPICNumbers = () => {
        dispatch(toggleLoading('picNumberList', true));

        return axiosRequestPICNumbers(customerID)
            .then((response) => {
                const picNumbers = convertPICNumberListingToAReadableFormat(
                    response.data.data.readPICNumbers
                );
                dispatch(
                    setUserInfo({
                        availablePICNumbers: Object.values(picNumbers),
                    })
                );

                dispatch(toggleLoading('picNumberList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('picNumberList', false));
            });
    };
    const requestAddresses = () => {
        dispatch(toggleLoading('addressList', true));

        return axiosRequestAddresses(customerID)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );
                dispatch(setUserInfo(userInfo));

                dispatch(toggleLoading('addressList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('addressList', false));
            });
    };
    const requestDefaultBillingAddressID = () => {
        dispatch(toggleLoading('addressList', true));

        return axiosRequestDefaultBillingAddressID(customerID)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );
                dispatch(setUserInfo(userInfo));

                dispatch(toggleLoading('addressList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('addressList', false));
            });
    };
    const submitAccountHolderInformation = (formData, callback) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosUpdateAccountHolderInformation(formData, customerID)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.updateMemberInfo
                );
                dispatch(setUserInfo(userInfo));

                createAlert(
                    {
                        message: getTranslation(
                            'ACCOUNT_HOLDER_INFORMATION_FORM.SUCCESS_MESSAGE'
                        ),
                    },
                    'success'
                );

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };

    const getFormProps = () => {
        let finalProps = {
            // Basic
            initialized: state.initialized,
            isLoading: state.isLoading,
            memberType: memberType === 'wholesaler' ? 'reseller': 'retail-customer',
            userInfo: getUserFullInfo(state.info, addressList),
            globalConfig,

            requestInfo: requestInfo,
            submitAccountHolderInformation: submitAccountHolderInformation,

            // Address Related
            createAddress: (formData, callback) => {
                const successCallback =
                    typeof callback.success != 'undefined'
                        ? callback.success
                        : null;
                const errorCallback =
                    typeof callback.error != 'undefined'
                        ? callback.error
                        : null;
                const alwaysCallback =
                    typeof callback.always != 'undefined'
                        ? callback.always
                        : null;

                return axiosCreateAddress(formData, customerID)
                    .then((response) => {
                        requestAddresses();

                        // Callbacks
                        if (successCallback) successCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    })
                    .catch((response) => {
                        handleAxiosErrors(response);

                        // Callbacks
                        if (errorCallback) errorCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    });
            },
            makeDefaultBillingAddress: (id, callback) => {
                const newCallback = {
                    success: (response) => {
                        if (typeof callback.success != 'undefined') {
                            callback.success(response);
                        }

                        requestDefaultBillingAddressID();
                    },
                    error: (response) => {
                        if (typeof callback.error != 'undefined') {
                            callback.error(response);
                        }
                    },
                    always: (response) => {
                        if (typeof callback.always != 'undefined') {
                            callback.always(response);
                        }
                    },
                };

                return submitAccountHolderInformation(
                    { BillingAddressID: id },
                    newCallback
                );
            },
            deleteAddress: (id, callback) => {
                const successCallback =
                    typeof callback.success != 'undefined'
                        ? callback.success
                        : null;
                const errorCallback =
                    typeof callback.error != 'undefined'
                        ? callback.error
                        : null;
                const alwaysCallback =
                    typeof callback.always != 'undefined'
                        ? callback.always
                        : null;

                return axiosDeleteAddress(id, customerID)
                    .then((response) => {
                        requestAddresses();

                        // Callbacks
                        if (successCallback) successCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    })
                    .catch((response) => {
                        handleAxiosErrors(response);

                        // Callbacks
                        if (errorCallback) errorCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    });
            },

            // PIC Number Related
            createPICNumber: (formData, callback) => {},
            makeDefaultPICNumber: (picNumberID, callback) => {
                const successCallback =
                    typeof callback.success != 'undefined'
                        ? callback.success
                        : null;
                const errorCallback =
                    typeof callback.error != 'undefined'
                        ? callback.error
                        : null;
                const alwaysCallback =
                    typeof callback.always != 'undefined'
                        ? callback.always
                        : null;

                return axioxMakeDefaultPICNumber(picNumberID, customerID)
                    .then((response) => {
                        requestPICNumbers();

                        // Callbacks
                        if (successCallback) successCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    })
                    .catch((response) => {
                        handleAxiosErrors(response);

                        // Callbacks
                        if (errorCallback) errorCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    });
            },
            deletePICNumber: (picNumberID, callback) => {
                const successCallback =
                    typeof callback.success != 'undefined'
                        ? callback.success
                        : null;
                const errorCallback =
                    typeof callback.error != 'undefined'
                        ? callback.error
                        : null;
                const alwaysCallback =
                    typeof callback.always != 'undefined'
                        ? callback.always
                        : null;

                return axiosDeletePICNumber(picNumberID, customerID)
                    .then((response) => {
                        requestPICNumbers();

                        // Callbacks
                        if (successCallback) successCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    })
                    .catch((response) => {
                        handleAxiosErrors(response);

                        // Callbacks
                        if (errorCallback) errorCallback(response);
                        if (alwaysCallback) alwaysCallback(response);
                    });
            },
        };

        return finalProps;
    };

    // Initial request
    useEffect(() => {
        requestInfo();
    }, []);

    return (
        <React.Fragment>
            <Form {...getFormProps()} />
        </React.Fragment>
    );
}

export default CustomerDetailForm;
